class SliderComponent extends HTMLElement {
    constructor() {
        super();
        this.slider = this.querySelector('[id^="Slider-"]');
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.enableSliderLooping = false;
        this.currentPageElement = this.querySelector('.slider-counter--current');
        this.pageTotalElement = this.querySelector('.slider-counter--total');
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');

        if (!this.slider || !this.nextButton) return;

        this.initPages();
        const resizeObserver = new ResizeObserver(entries => this.initPages());
        resizeObserver.observe(this.slider);

        this.slider.addEventListener('scroll', this.update.bind(this), { passive: true });
        this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
        this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    initPages() {
        this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
        if (this.sliderItemsToShow.length < 2) return;
        this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
        this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
        this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
        this.update();
    }

    resetPages() {
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.initPages();
    }

    update() {
        const previousPage = this.currentPage;
        this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

        if (this.currentPageElement && this.pageTotalElement) {
            this.currentPageElement.textContent = this.currentPage;
            this.pageTotalElement.textContent = this.totalPages;
        }

        if (this.currentPage != previousPage) {
            this.dispatchEvent(new CustomEvent('slideChanged', {
                detail: {
                    currentPage: this.currentPage,
                    currentElement: this.sliderItemsToShow[this.currentPage - 1]
                }
            }));
        }

        if (this.enableSliderLooping) return;

        if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
            this.prevButton.setAttribute('disabled', 'disabled');
            this.prevButton.classList.add('removed')
        } else {
            this.prevButton.removeAttribute('disabled');
        }

        if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
            this.nextButton.setAttribute('disabled', 'disabled');
        } else {
            this.nextButton.removeAttribute('disabled');
        }
    }

    isSlideVisible(element, offset = 0) {
        const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
        return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
    }

    onButtonClick(event) {
        event.preventDefault();
        const step = event.currentTarget.dataset.step || 1;
        this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + (step * this.sliderItemOffset) : this.slider.scrollLeft - (step * this.sliderItemOffset);
        this.slider.scrollTo({
            left: this.slideScrollPosition
        });
    }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
    constructor() {
        super();
        this.sliderControlWrapper = this.querySelector('.slider-buttons');
        this.enableSliderLooping = true;

        if (!this.sliderControlWrapper) return;

        this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
        if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

        this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
        this.sliderControlLinksArray.forEach(link => link.addEventListener('click', this.linkToSlide.bind(this)));
        this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this), { passive: true });
        this.setSlideVisibility();

        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
    }

    setAutoPlay() {
        this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
        this.autoplaySpeed = this.slider.dataset.speed * 1000;

        this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this), { passive: true });
        this.addEventListener('mouseover', this.focusInHandling.bind(this), { passive: true });
        this.addEventListener('mouseleave', this.focusOutHandling.bind(this), { passive: true });
        this.addEventListener('focusin', this.focusInHandling.bind(this), { passive: true });
        this.addEventListener('focusout', this.focusOutHandling.bind(this), { passive: true });

        this.play();
        this.autoplayButtonIsSetToPlay = true;
    }

    onButtonClick(event) {
        super.onButtonClick(event);
        const isFirstSlide = this.currentPage === 1;
        const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

        if (!isFirstSlide && !isLastSlide) return;

        if (isFirstSlide && event.currentTarget.name === 'previous') {
            this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
        } else if (isLastSlide && event.currentTarget.name === 'next') {
            this.slideScrollPosition = 0;
        }
        this.slider.scrollTo({
            left: this.slideScrollPosition
        });
    }

    update() {
        super.update();
        this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
        this.prevButton.removeAttribute('disabled');

        if (!this.sliderControlButtons.length) return;

        this.sliderControlButtons.forEach(link => {
            link.classList.remove('slider-counter__link--active');
            link.removeAttribute('aria-current');
        });
        this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
        this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
    }

    autoPlayToggle() {
        this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
        this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
        this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
    }

    focusOutHandling(event) {
        const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
        if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
        this.play();
    }

    focusInHandling(event) {
        const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
        if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
            this.play();
        } else if (this.autoplayButtonIsSetToPlay) {
            this.pause();
        }
    }

    play() {
        this.slider.setAttribute('aria-live', 'off');
        clearInterval(this.autoplay);
        this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
    }

    pause() {
        this.slider.setAttribute('aria-live', 'polite');
        clearInterval(this.autoplay);
    }

    togglePlayButtonState(pauseAutoplay) {
        if (pauseAutoplay) {
            this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
            this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
        } else {
            this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
            this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
        }
    }

    autoRotateSlides() {
        const slideScrollPosition = this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.slider.querySelector('.slideshow__slide').clientWidth;
        this.slider.scrollTo({
            left: slideScrollPosition
        });
    }

    setSlideVisibility() {
        this.sliderItemsToShow.forEach((item, index) => {
            const button = item.querySelector('a');
            if (index === this.currentPage - 1) {
                if (button) button.removeAttribute('tabindex');
                item.setAttribute('aria-hidden', 'false');
                item.removeAttribute('tabindex');
            } else {
                if (button) button.setAttribute('tabindex', '-1');
                item.setAttribute('aria-hidden', 'true');
                item.setAttribute('tabindex', '-1');
            }
        });
    }

    linkToSlide(event) {
        event.preventDefault();
        const slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
        this.slider.scrollTo({
            left: slideScrollPosition
        });
    }
}

customElements.define('slideshow-component', SlideshowComponent);

class FeatureCarousel extends HTMLElement {
    constructor() {
        super();
        this.offset = 3;
        this.currentIndex = 0;
        this.dragingStart = false;
        this.currentSlideSize = 0;

        this.startX = -1;
        this.drag = {
            x: 0,
            y: 0,
        };

        this.elements = {
            mainSlider: this.querySelector(".ml_feature_scroll ul"),
            actionsHolder: this.querySelector(".ml_feature_buttons"),
            counterHolder: this.querySelector(".ml_feature_counter"),
            slides: this.querySelectorAll(".ml_feature_scroll ul li"),
            mainSliderHolder: this.querySelector(".ml_feature_scroll"),
            indicators: this.querySelectorAll("ul.ml_feature_indication li"),
            indicatorHolder: this.querySelector("ul.ml_feature_indication"),
            next: this.querySelector(`.ml_feature_buttons #next_${this.dataset.section}`),
            prev: this.querySelector(`.ml_feature_buttons #previous_${this.dataset.section}`),
            counterTotal: this.querySelector(".ml_feature_counter .ml_feature_counter_total"),
            counterCurrent: this.querySelector(".ml_feature_counter .ml_feature_counter_current")
        };

        this.elements.mainSlider.addEventListener('wheel', debounce(this.onDragChanged.bind(this), 500), { passive: true });
        this.elements.mainSlider.addEventListener('dragover', debounce(this.onDragChanged.bind(this), 500), { passive: true });
        this.elements.mainSlider.addEventListener('dragstart', debounce(this.onDragChanged.bind(this), 500), { passive: true });

        // this.elements.mainSlider.addEventListener('touchend', debounce(this.onDragChanged.bind(this), 500), { passive: true });
        // this.elements.mainSlider.addEventListener('touchstart', debounce(this.onDragChanged.bind(this), 500), { passive: true });

        this.elements.mainSlider.addEventListener('mouseup', debounce(this.onDragChanged.bind(this), 0), { passive: true });
        this.elements.mainSlider.addEventListener('mouseover', debounce(this.onDragChanged.bind(this), 0), { passive: true });
        this.elements.mainSlider.addEventListener('mousedown', debounce(this.onDragChanged.bind(this), 0), { passive: true });
        this.elements.mainSlider.addEventListener('mouseleave', debounce(this.onDragChanged.bind(this), 0), { passive: true });

        this.elements.mainSliderHolder.addEventListener('touchmove', this.onDragChanged.bind(this), { passive: true });
        this.elements.mainSliderHolder.addEventListener('touchend', debounce(this.onDragChanged.bind(this)), { passive: true });
        this.elements.mainSliderHolder.addEventListener('touchstart', debounce(this.onDragChanged.bind(this)), { passive: true });
        this.elements.mainSliderHolder.addEventListener('touchcancel', debounce(this.onDragChanged.bind(this)), { passive: true });

        this.attachClickToIndicators();

        this.elements.next?.addEventListener("click", (e) => {
            this.nextItem();
        }, { passive: true });

        this.elements.prev?.addEventListener("click", (e) => {
            this.prevItem();
        }, { passive: true });

        this.calculateOffset();

        window.addEventListener("resize", (e) => {
            const prevIndex = this.offset;
            this.calculateOffset();
            if (prevIndex < this.offset) {
                this.currentIndex = (this.currentIndex - this.offset) < 0 ? 0 : this.currentIndex - this.offset;
            } else {
                const slidesLen = (this.elements.slides.length - this.offset) < 0 ? 0 : this.elements.slides.length - this.offset;
                this.currentIndex = (this.currentIndex + this.offset) % slidesLen;
            }
            this.handlesliderPosition();
        }, { passive: true })

        window.addEventListener("scroll", (e) => {
            if(window.ml_dragging) {
                e.preventDefault();
            }
        } , { passive: true })
    }

    get isRtl() {
        return getComputedStyle(document.documentElement).direction === 'rtl';
    }

    attachClickToIndicators() {
        this.elements.indicators?.forEach((el) => {
            el.addEventListener("click", (e) => {
                this.currentIndex = el.dataset?.index;
                this.handlesliderPosition();
            }, { passive: true });
        });
    }

    onDragChanged(event) {
        const dirSign = this.isRtl ? 1 : -1;
        if (event.type == 'mouseover' || event.type == 'touchmove') {
            if (this.dragingStart && this.startX != -1) {
                const slideWidth = (this.elements.slides?.[this.currentIndex]?.clientWidth || 0) + (+this.dataset?.horizontalspace);
                const offset = this.startX - (event.x || event.changedTouches?.[0]?.pageX);
                this.elements.mainSlider.style.transform = `translate3d(${(slideWidth * this.currentIndex * dirSign) + (offset * -2)}px, 0px, 0px)`;
            }
        } else if (event.type == 'mousedown' || event.type == 'touchstart') {
            this.classList.add("dragging");
            window.ml_dragging = true;
            this.startX = event.x || event.changedTouches?.[0]?.pageX;
            this.dragingStart = true;
            this.style.cursor = "grabbing";
        } else if (event.type == 'mouseup' || event.type == 'mouseleave' || event.type == 'touchend' || event.type == 'touchcancel') {
            this.startX = -1;
            this.style.cursor = "grab";
            this.dragingStart = false;
            const finalTranslateX = +this.elements.mainSlider.style.transform.replace("px", "").replace("translate3d(", "").replace(", 0px, 0px)", "");
            const outOfRange = this.isRtl ? finalTranslateX < 0 : finalTranslateX > 0;
            if (outOfRange) {
                this.elements.mainSlider.style.transform = `translate3d(0px, 0px, 0px)`;
            } else {
                const slideWidth = (this.elements.slides?.[this.currentIndex]?.clientWidth || 0) + (+this.dataset?.horizontalspace);
                this.currentIndex = Math.round(Math.abs(finalTranslateX / slideWidth));
                const slidesLen = (this.elements.slides.length - this.offset) < 0 ? 0 : this.elements.slides.length - this.offset;
                if (this.currentIndex > (slidesLen - 1)) {
                    this.currentIndex = slidesLen - 1;
                }
                this.handlesliderPosition();
            }
            this.classList.remove("dragging");
            window.ml_dragging = false;
        } else if (event.type == 'dragstart') {
            this.classList.add("dragging");
            window.ml_dragging = true;
            this.drag = {
                x: event.x,
                y: event.y
            }
        } else {
            if (event.type == 'wheel') {
                if (event.deltaX != 0) {
                    const forward = this.isRtl ? event.deltaX < 0 : event.deltaX > 0;
                    if (forward) {
                        this.nextItem();
                    } else {
                        this.prevItem();
                    }
                }
            } else {
                if (this.drag.x != event.x) {
                    const backward = this.isRtl ? (this.drag.x - event.x) > 0 : (this.drag.x - event.x) < 0;
                    if (backward) {
                        this.prevItem();
                    } else {
                        this.nextItem();
                    }
                }
                this.classList.remove("dragging");
                window.ml_dragging = false;
            }
        }
    }

    prevItem() {
        const slidesLen = (this.elements.slides.length - this.offset) < 0 ? 0 : this.elements.slides.length - this.offset;
        this.currentIndex = (this.currentIndex - 1) < 0 ? slidesLen - 1 : this.currentIndex - 1;
        this.handlesliderPosition();
    }

    nextItem() {
        const slidesLen = (this.elements.slides.length - this.offset) < 0 ? 0 : this.elements.slides.length - this.offset;
        this.currentIndex = (this.currentIndex + 1) % slidesLen;
        this.handlesliderPosition();
    }

    handlesliderPosition() {
        if (this.elements?.counterCurrent) this.elements.counterCurrent.innerHTML = this.currentIndex + 1;
        this.elements.indicators.forEach((el, index) => {
            el.classList.remove("active");
            if (index == this.currentIndex) {
                el.classList.add("active");
            }
        });
        if (this.currentIndex != 0) {
            const dirSign = this.isRtl ? 1 : -1;
            const slideWidth = (this.elements.slides?.[this.currentIndex]?.clientWidth || 0) + (+this.dataset?.horizontalspace);
            this.elements.mainSlider.style.transform = `translate3d(${dirSign * slideWidth * this.currentIndex}px, 0px, 0px)`;
        } else {
            this.elements.mainSlider.style.transform = `translate3d(0px, 0px, 0px)`;
        }
    }

    calculateOffset() {
        const mainWidth = this.elements.mainSlider?.clientWidth;
        if ((mainWidth || window.innerWidth) > 1000) {
            this.offset = 3;
        } else if ((mainWidth || window.innerWidth) <= 1000 && (mainWidth || window.innerWidth) > 778) {
            this.offset = 2;
        } else if ((mainWidth || window.innerWidth) <= 778 && (mainWidth || window.innerWidth) > 600) {
            this.offset = 1;
        } else {
            this.offset = 0;
        }
        const slidesLen = (this.elements.slides.length - this.offset) < 0 ? 0 : this.elements.slides.length - this.offset;

        if (this.elements?.counterTotal) this.elements.counterTotal.innerHTML = slidesLen;
        if (this.elements?.indicatorHolder) {
            const removables = [].slice.call(this.querySelectorAll("ul.ml_feature_indication li"));
            removables.forEach(el => {
                el.remove();
            })

            for (let index = 0; index < slidesLen; index++) {
                const li = document.createElement("li");
                li.dataset.index = index;
                li.tabIndex = "0";
                if (index == 0) {
                    li.classList.add("active");
                }
                li.onkeypress = (e) => {
                    if (e.key === "Enter") {
                        li.click();
                    }
                }
                this.elements?.indicatorHolder.append(li);
            }
            this.elements.indicators = this.querySelectorAll("ul.ml_feature_indication li");
            this.attachClickToIndicators();
        }

        if (this.elements?.indicators?.length <= 1) {
            this.elements.actionsHolder.style.opacity = 0;
            this.elements.actionsHolder.style.visibility = "hidden";
        } else {
            this.elements.actionsHolder.style.opacity = "";
            this.elements.actionsHolder.style.visibility = "";
        }
    }
}

customElements.define('feature-carousel', FeatureCarousel);
